import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AviporkLogo } from '../components/AviporkLogo';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { login, register } = useAuth();
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ nombre: '', email: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Ingrese correo y contraseña');
      return;
    }
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Bienvenido a AVIPORK');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerForm.nombre || !registerForm.email || !registerForm.password) {
      toast.error('Complete todos los campos');
      return;
    }
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (registerForm.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      const result = await register(registerForm.nombre, registerForm.email, registerForm.password);
      if (result?.pendiente_aprobacion) {
        toast.success(
          result.message || 'Tu solicitud fue enviada. El Super Administrador debe aprobarla antes de que puedas acceder.',
          { duration: 10000 },
        );
        // Limpia el form y deja al usuario en la pantalla de login.
        setRegisterForm({ nombre: '', email: '', password: '', confirmPassword: '' });
      } else {
        toast.success('Cuenta creada exitosamente.', { duration: 6000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAF8] px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: '#006400' }}>
            <AviporkLogo compact color="#FFFFFF" />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color: '#006400' }}>AVIPORK</h1>
          <p className="text-sm text-muted-foreground">Crianza de Primera Calidad</p>
        </div>

        <Card className="shadow-lg">
          <Tabs defaultValue="login">
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="login" className="gap-2">
                <LogIn className="w-4 h-4" /> Iniciar Sesión
              </TabsTrigger>
              <TabsTrigger value="register" className="gap-2">
                <UserPlus className="w-4 h-4" /> Registrarse
              </TabsTrigger>
            </TabsList>

            {/* Login */}
            <TabsContent value="login">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Iniciar Sesión</CardTitle>
                <CardDescription>Ingrese sus credenciales para acceder</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">Correo electrónico</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="h-11 mt-1.5"
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">Contraseña</Label>
                    <div className="relative mt-1.5">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Ingrese su contraseña"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className="h-11 pr-10"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit">
                    {loading ? 'Ingresando...' : 'Iniciar Sesión'}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>

            {/* Register */}
            <TabsContent value="register">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Crear Cuenta</CardTitle>
                <CardDescription>Registre una nueva cuenta de usuario</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="reg-nombre">Nombre completo</Label>
                    <Input
                      id="reg-nombre"
                      placeholder="Juan Pérez"
                      value={registerForm.nombre}
                      onChange={(e) => setRegisterForm({ ...registerForm, nombre: e.target.value })}
                      className="h-11 mt-1.5"
                      data-testid="register-nombre"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-email">Correo electrónico</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      className="h-11 mt-1.5"
                      data-testid="register-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-password">Contraseña</Label>
                    <Input
                      id="reg-password"
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      className="h-11 mt-1.5"
                      data-testid="register-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-confirm">Confirmar contraseña</Label>
                    <Input
                      id="reg-confirm"
                      type="password"
                      placeholder="Repita la contraseña"
                      value={registerForm.confirmPassword}
                      onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                      className="h-11 mt-1.5"
                      data-testid="register-confirm"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={loading} data-testid="register-submit">
                    {loading ? 'Registrando...' : 'Crear Cuenta'}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          AVIPORK &copy; {new Date().getFullYear()} - Sistema de Gestión Avícola
        </p>
      </div>
    </div>
  );
}
